import { fromDisplayKmh, toDisplayKmh } from '../constants.ts';
import { clamp } from '../math.ts';
import type { ReachAxes } from './reach.ts';

/** Stretch is full here. The Headreach meter reads this, and it does not grow past it. */
const HEADREACH_REF_KMH = 150;
/** A nose click cannot land beyond this. The headreach dial shows the same cap. */
export const HEADREACH_TOP_KMH = 300;
/** Added to authored accelerate at full stretch: 1.75×. */
const ACCEL_STRETCH = 0.75;
/** Removed from authored turn at full stretch: 0.75×. */
const TURN_PINCH = 0.25;
/**
 * Brake growth from 150 km/h up to the cap. At 300 km/h the brake is 5× the
 * authored length, so two full slows on a 30 m brake fall back through 150.
 */
const BRAKE_SURPLUS = 4;

/** Coast and reaches after headreach. Same shape callers already pass on from planing. */
export type HeadreachProjection = {
  /** Always 0. Headreach has no plane to fill. */
  level: number;
  coastPx: number;
  axes: ReachAxes;
};

/** Smoothstep. Flat at both ends, so a small speed step cannot open a new nose. */
function smoothstep(t: number): number {
  const x = clamp(t, 0, 1);

  return x * x * (3 - 2 * x);
}

/** 0 at rest, 1 at 150 km/h and above. The telemetry bar reads this. */
export function headreachLevel(speedPx: number): number {
  const speed = Math.max(0, speedPx);

  return smoothstep(toDisplayKmh(speed) / HEADREACH_REF_KMH);
}

/**
 * Speed lengthens the next ring gradually up to 150 km/h. Coast is the full
 * speed. Above that the oval stays fully stretched, the brake grows, and the
 * forward reach closes so a nose click cannot pass 300 km/h.
 * Deleting this module means callers pass `boat.speed` and the authored reaches.
 */
export function headreachFeel(speedPx: number, authored: ReachAxes): HeadreachProjection {
  const speed = Math.max(0, speedPx);
  const stretch = headreachLevel(speed);
  const speedKmh = toDisplayKmh(speed);
  const surplus = clamp(
    (speedKmh - HEADREACH_REF_KMH) / (HEADREACH_TOP_KMH - HEADREACH_REF_KMH),
    0,
    1,
  );
  const room = Math.max(0, fromDisplayKmh(HEADREACH_TOP_KMH) - speed);
  const stretchedAccel = authored.accel * (1 + ACCEL_STRETCH * stretch);

  return {
    level: 0,
    coastPx: speed,
    axes: {
      turn: authored.turn * (1 - TURN_PINCH * stretch),
      accel: Math.min(stretchedAccel, room),
      brake: authored.brake * (1 + BRAKE_SURPLUS * surplus),
    },
  };
}
